'use client';

import { c as _c } from "react/compiler-runtime";
import './nav-tabs.scss';
import { useEffect, useRef } from 'react';
import { HorizontalScrollArea } from '../../atoms/horizontal-scroll-area/horizontal-scroll-area.tsx';
import { NavLink } from '../../atoms/link/nav-link.tsx';

/**
 * Positions the active tab within the visible scroll area on mobile.
 * For now we're just applying this for all screen sizes if focusOnMobile is true.
 */
const anchorActiveTab = (container: HTMLDivElement, list: HTMLDivElement): void => {
  // TODO: NavTabs is unmounted with each route change, so this can't persist the current scroll pos
  if (!container || !list?.childElementCount) {
    return;
  }

  // Find the active tab. NavLink and the isActive override both emit
  // aria-current="page", so match that value.
  const activeTab = list.querySelector<HTMLElement>('[aria-current="page"]');
  if (!activeTab) {
    return;
  }

  // This runs when mounted, if the last tab is visible, get out
  const lastTab = list.children[list.childElementCount - 1];
  const lastRect = lastTab.getBoundingClientRect();
  const isFullyVisible = lastRect.right <= container.getBoundingClientRect().right;
  if (isFullyVisible) {
    return;
  }
  const containerWidth = container.clientWidth;
  const maxScrollLeft = Math.max(0, container.scrollWidth - containerWidth);
  const tabOffsetLeft = activeTab.offsetLeft;
  const tabWidth = activeTab.offsetWidth;
  let targetLeft = tabOffsetLeft - (containerWidth - tabWidth) / 2;
  if (lastTab === activeTab) {
    targetLeft = tabOffsetLeft + tabWidth - containerWidth;
  }
  container.scrollLeft = Math.min(Math.max(targetLeft, 0), maxScrollLeft);
};
export type LinkPlaceholder = {
  id: string;
  title: string;
  path: string;
  /**
   * In almost all cases this should be automatically handled by the Link component.
   */
  isActive?: boolean;
};
export type NavTabsProps = {
  links: LinkPlaceholder[];
  focusOnMobile?: boolean;
};

// TODO: accessibility considerations we need to have in mind for these tertiary navs
export const NavTabs: React.FC<NavTabsProps> = t0 => {
  const $ = _c(11);
  const {
    links,
    focusOnMobile: t1
  } = t0;
  const focusOnMobile = t1 === undefined ? true : t1;
  const scrollContainerRef = useRef(null);
  const listRef = useRef(null);
  let t2;
  if ($[0] !== links) {
    t2 = JSON.stringify(links.map(_temp));
    $[0] = links;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const tabAnchoringKey = t2;
  let t3;
  if ($[2] !== focusOnMobile) {
    t3 = () => {
      if (!focusOnMobile) {
        return;
      }
      const container = scrollContainerRef.current;
      const list = listRef.current;
      if (!container || !list) {
        return;
      }
      anchorActiveTab(container, list);
    };
    $[2] = focusOnMobile;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== focusOnMobile || $[5] !== tabAnchoringKey) {
    t4 = [focusOnMobile, tabAnchoringKey];
    $[4] = focusOnMobile;
    $[5] = tabAnchoringKey;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t3, t4);
  if (!links || links.length === 0) {
    return null;
  }
  let t5;
  if ($[7] !== links) {
    t5 = links.map(_temp2);
    $[7] = links;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <nav className="NavTabs my-comfy-xl relative w-100 overflow-hidden"><HorizontalScrollArea><div ref={scrollContainerRef} className="NavTabs__scrollcontainer overflow-x-auto"><div ref={listRef} className="NavTabs__list flex gap-comfy-md">{t5}</div></div></HorizontalScrollArea></nav>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
function _temp(t0) {
  const {
    id,
    path,
    title,
    isActive
  } = t0;
  return [id, path, title, isActive];
}
function _temp2(link) {
  return <NavLink key={link.id} href={link.path} className="NavTabs__tab label-potent label-lg pr-snug-md pb-comfy-xs pl-snug-md flex-shrink-0" {...link.isActive && {
    "aria-current": "page"
  }}>{link.title}</NavLink>;
}